import { ProductInfo } from "@insite/client-framework/Common/ProductInfo";
import waitFor from "@insite/client-framework/Common/Utilities/waitFor";
import { createHandlerChainRunner, Handler } from "@insite/client-framework/HandlerCreator";
import loadRealTimeInventory from "@insite/client-framework/Store/CommonHandlers/LoadRealTimeInventory";
import loadRealTimePricing from "@insite/client-framework/Store/CommonHandlers/LoadRealTimePricing";
import { UploadedItem } from "@insite/client-framework/Store/Components/OrderUpload/OrderUploadState";
import clearProduct from "@insite/client-framework/Store/Components/ProductSelector/Handlers/ClearProduct";
import reset from "@insite/client-framework/Store/Components/ProductSelector/Handlers/Reset";
import searchProducts from "@insite/client-framework/Store/Components/ProductSelector/Handlers/SearchProducts";
import setProduct from "@insite/client-framework/Store/Components/ProductSelector/Handlers/SetProduct";
import { getProductState } from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import { ProductDto, ProductModel } from "@insite/client-framework/Types/ApiModels";

type Parameter = {
    products: ProductDto[];
    uploadedItems: UploadedItem[];
    onSuccess: () => void;
};

type Props = {
    productsInfo: ProductInfo[];
};

type HandlerType = Handler<Parameter, Props>;

export const DispatchLoadingMultipleProducts: HandlerType = props => {
    props.dispatch({
        type: "Custom/ProductInformation/LoadingMultipleProducts",
        isLoading: true,
    } as any);

    props.dispatch({
        type: "Custom/ProductInformation/AddMultipleProductsCompleted",
        addedMultipleProducts: false,
    } as any);
};

export const DispatchBeginAddProduct: HandlerType = props => {
    props.dispatch({
        type: "Pages/QuickOrder/BeginAddProduct",
    });
};

export const CopyCurrentValues: HandlerType = props => {
    props.productsInfo = props.parameter.products
        .filter(product => !product.quoteRequired)
        .map(product => {
            const qtyOrdered =
                props.parameter.uploadedItems.find(item => item.name === product.erpNumber)?.qtyOrdered ?? 0;
            return {
                productId: product.id,
                qtyOrdered,
                unitOfMeasure: product.unitOfMeasure,
                productDetailPath: product.productDetailUrl,
            } as ProductInfo;
        });
};

export const ChangeProductQtyForExistingProduct: HandlerType = props => {
    props.productsInfo.forEach(product => {
        const quickOrderProductInfos = props.getState().pages.quickOrder.productInfos;
        const { productId, qtyOrdered, unitOfMeasure } = product;
        const existingProductInfo = quickOrderProductInfos.find(
            o => o.productId === productId && o.unitOfMeasure === unitOfMeasure,
        );
        if (!existingProductInfo) {
            return;
        }
        product.qtyOrdered = existingProductInfo.qtyOrdered + qtyOrdered;
    });
};

export const GetPrice: HandlerType = async props => {
    let loadedPricing = false;

    const productPriceParameters = props.productsInfo.map(({ productId, unitOfMeasure, qtyOrdered }) => ({
        productId,
        unitOfMeasure,
        qtyOrdered,
    }));

    props.dispatch(
        loadRealTimePricing({
            productPriceParameters,
            onComplete: realTimePricingProps => {
                if (realTimePricingProps.apiResult) {
                    const apiResults = realTimePricingProps.apiResult.realTimePricingResults;
                    props.productsInfo.forEach(productInfo => {
                        const realTime = apiResults?.find(o => o.productId === productInfo.productId);
                        productInfo.pricing = realTime;
                    });
                } else if (realTimePricingProps.error) {
                    props.productsInfo.forEach(productInfo => {
                        productInfo.failedToLoadPricing = true;
                    });
                }
                loadedPricing = true;
            },
        }),
    );

    await waitFor(() => loadedPricing);
};

export const GetInventory: HandlerType = async props => {
    let loadedInventory = false;

    const productIds = props.productsInfo.map(({ productId }) => productId);

    props.dispatch(
        loadRealTimeInventory({
            productIds,
            onComplete: realTimeInventoryProps => {
                if (realTimeInventoryProps.apiResult) {
                    const apiResults = realTimeInventoryProps.apiResult.realTimeInventoryResults;
                    props.productsInfo.forEach(productInfo => {
                        const realTime = apiResults?.find(o => o.productId === productInfo.productId);
                        productInfo.inventory = realTime;
                    });
                } else if (realTimeInventoryProps.error) {
                    props.productsInfo.forEach(productInfo => {
                        productInfo.failedToLoadInventory = true;
                    });
                }
                loadedInventory = true;
            },
        }),
    );

    await waitFor(() => loadedInventory);
};

export const DispatchSetProduct: HandlerType = async props => {
    const state = props.getState();
    const products = state.pages.quickOrder.productInfos.map(o => getProductState(state, o.productId).value) as (
        | ProductModel
        | undefined
    )[];

    props.productsInfo.forEach(productInfo => {
        props.dispatch(
            setProduct({
                productId: productInfo.productId,
                validateProduct: false,
                skipInventoryValidation: true,
            }),
        );
    });

    await waitFor(() => products.length > 0);
};

export const DispatchCompleteAddProduct: HandlerType = props => {
    props.productsInfo.forEach(productInfo => {
        props.dispatch({
            type: "Pages/QuickOrder/CompleteAddProduct",
            productInfo,
        });
    });
};

export const DispatchCompleteProcess: HandlerType = props => {
    props.dispatch(reset());
    props.dispatch(clearProduct());
    props.dispatch(searchProducts({ query: "" }));

    props.dispatch({
        type: "Custom/ProductInformation/LoadingMultipleProducts",
        isLoading: false,
    } as any);

    // set the state to AddMultipleProducts to true
    props.dispatch({
        type: "Custom/ProductInformation/AddMultipleProductsCompleted",
        addedMultipleProducts: true,
    } as any);

    props.parameter.onSuccess();
};

export const chain = [
    DispatchLoadingMultipleProducts,
    DispatchBeginAddProduct,
    CopyCurrentValues,
    ChangeProductQtyForExistingProduct,
    GetPrice,
    GetInventory,
    DispatchSetProduct,
    DispatchCompleteAddProduct,
    DispatchCompleteProcess,
];

const addMultipleProducts = createHandlerChainRunner(chain, "AddMultipleProducts");
export default addMultipleProducts;
